import { createSelector } from '@reduxjs/toolkit';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectMatchedTaxonomyEntry } from './selectMatchedTaxonomyEntry';

export interface TaxonomyHeaderContent {
  description?: string;
  heading?: string;
  subheading?: string;
}

const selectCurrentPage = (state: ReduxApplicationState) => state.productsQuery?.page;

/**
 * Content for the taxonomy header block on the shop route. Sourced from the
 * matched neighborhood entry's content (heading/subheading/intro copy), which
 * tracks the selected entry as filters/navigation change client-side. Keeping
 * coherent copy on screen during content fetches is handled by the container's
 * bridge (`TaxonomyHeaderBlockContainer`), not here.
 *
 * Intro copy is shown only on the first PLP page, keyed off the logical page
 * number (`productsQuery.page`) rather than `products.pages` membership: the
 * latter is emptied during the products refetch on navigation, which made the
 * copy flicker. This is shop-only; the category route uses
 * `selectHeaderCopyBlockContent`.
 */
export const selectTaxonomyHeaderContent = createSelector(
  [selectMatchedTaxonomyEntry, selectCurrentPage],
  (matchedEntry, page): TaxonomyHeaderContent => {
    const content = matchedEntry?.content;

    if (!content?.heading) {
      return {};
    }

    return {
      heading: content.heading,
      subheading: content.subheading,
      description: page === 1 ? content.introCopy : undefined,
    };
  },
);
